import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  remainingStreamLoadEtaMs,
  streamLoadProgressPercent,
} from '../../utils/streamLoadEta';

export type StreamLoadProgressProps = {
  label: string;
  startedAtMs: number;
  etaMs: number;
  className?: string;
};

export function StreamLoadProgress({
  label,
  startedAtMs,
  etaMs,
  className,
}: StreamLoadProgressProps) {
  const displayedPercentRef = useRef(4);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    displayedPercentRef.current = 4;
    setNowMs(Date.now());
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 250);
    return () => window.clearInterval(timer);
  }, [startedAtMs, etaMs]);

  const remainingMs = remainingStreamLoadEtaMs(startedAtMs, etaMs, nowMs);
  const computed = streamLoadProgressPercent(startedAtMs, etaMs, nowMs);
  const overtime = remainingMs <= 0;
  const percent = overtime
    ? Math.min(95, Math.max(displayedPercentRef.current + 0.4, computed))
    : Math.max(displayedPercentRef.current, computed);
  displayedPercentRef.current = percent;

  const etaText = overtime
    ? '比平时稍慢，还在准备…'
    : remainingMs <= 2_000
      ? '即将就绪…'
      : `大约还要 ${Math.max(1, Math.round(remainingMs / 1000))} 秒`;

  return (
    <div className={`mx-auto flex w-full max-w-sm flex-col items-center gap-3 ${className || ''}`}>
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-center text-base font-semibold text-ink">{etaText}</p>
      <p className="text-center text-sm text-muted">{label}</p>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-canvas"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
